/**
 * Setting a shareholder's share count is a ledger write, not a field update.
 *
 * Shares are not an attribute of a shareholder — they are the signed sum of that
 * holder's ledger events (see `cap_table_summary`). So a UI that lets someone
 * type "250,000" has to turn that into an event, and this decides which one and
 * for how many shares.
 *
 * The rules answer to `ledger_events_shares_check`, which is the database's
 * statement of the sign convention: ISSUANCE and SAFE_CONVERSION must be
 * positive, CANCELLATION negative, TRANSFER and ADJUSTMENT either but never
 * zero. Every plan this returns satisfies it.
 */

import { parseShareCount } from './shareCount';

export type ShareLedgerEventType = 'ISSUANCE' | 'ADJUSTMENT' | 'CANCELLATION';

/**
 * Why a holding is going down. The two are different acts and the register has
 * to say which: cancelling shares is a corporate act that retires them, whereas
 * a correction says the balance was recorded wrongly and never should have read
 * that way. Nothing in the numbers distinguishes them, so the user is asked.
 */
export type ReductionReason = 'CANCELLATION' | 'CORRECTION';

export type ShareBalancePlan =
  /** The balance is untouched. Not an error — the caller writes no ledger event. */
  | { kind: 'unchanged' }
  /** The input cannot be read as a share count. `error` is shown to the user. */
  | { kind: 'invalid'; error: string }
  /** A reduction with no reason given. Ask, then call again with one. */
  | { kind: 'needs-reason'; reducingBy: number }
  /** Write this event to move the balance to the target. */
  | {
      kind: 'change';
      eventType: ShareLedgerEventType;
      /** Signed delta, never zero. */
      shares: number;
      noteReference: string;
    };

export function planShareBalanceChange(
  /** Present holding. Accepts a string because Postgres numeric arrives as one. */
  currentShares: string | number,
  /** What the user typed. */
  target: string | number,
  /** Required only when the target is below the present holding. */
  reductionReason?: ReductionReason
): ShareBalancePlan {
  const current = parseShareCount(currentShares);
  if (current.kind === 'invalid') {
    return { kind: 'invalid', error: 'Current share balance is unavailable' };
  }

  // A blank field on a zero balance is an untouched field, not an attempt to set
  // nothing. Blanking a real balance is ambiguous, so it is rejected rather than
  // read as "cancel everything".
  if (typeof target === 'string' && target.trim() === '' && current.value === 0) {
    return { kind: 'unchanged' };
  }

  const parsed = parseShareCount(target);
  if (parsed.kind === 'invalid') return { kind: 'invalid', error: parsed.error };
  if (parsed.value < 0) return { kind: 'invalid', error: 'Shares cannot be negative' };

  const delta = parsed.value - current.value;
  if (delta === 0) return { kind: 'unchanged' };

  // A holder with nothing on the register is receiving shares, not having a
  // balance corrected. Recording that as an ADJUSTMENT made every founder's
  // opening holding read as a correction in the statutory register.
  if (current.value === 0) {
    return {
      kind: 'change',
      eventType: 'ISSUANCE',
      shares: delta,
      noteReference: `Initial issuance: ${parsed.value} shares`,
    };
  }

  if (delta > 0) {
    return {
      kind: 'change',
      eventType: 'ADJUSTMENT',
      shares: delta,
      noteReference: `Adjustment: ${current.value} → ${parsed.value} shares (+${delta})`,
    };
  }

  // Going down. Cancelling shares and correcting a mistyped balance produce the
  // same arithmetic and mean different things, so the caller has to say which
  // before anything is written.
  if (!reductionReason) {
    return { kind: 'needs-reason', reducingBy: -delta };
  }

  const movement = `${current.value} → ${parsed.value} shares (${delta})`;

  return reductionReason === 'CANCELLATION'
    ? {
        kind: 'change',
        eventType: 'CANCELLATION',
        shares: delta,
        noteReference: `Cancellation: ${movement}`,
      }
    : {
        kind: 'change',
        eventType: 'ADJUSTMENT',
        shares: delta,
        noteReference: `Adjustment: ${movement}`,
      };
}
